// Leave company and delete account (WEB_PLAN.md §4.7), as GroundTeam/Sync/SyncCenter.swift (isOnlyMember,
// leaveCompany, deleteAccount) and SupabaseSyncAPI.swift (leave_company, team_roster, delete-account) do them.

import type { Roster } from '@/contracts/wire';
import type { SessionCtx } from '@/session/types';
import { LastAdminError, ServerError } from './errors';
import { flushPending } from './flush';
import { callRpc, functionError, invokeFunction } from './rpc';

/**
 * Whether the signed-in person is the company's only member: leaving or deleting the account then deletes the
 * company and all its data on the server, so the confirmation must say so. Only admins can be the last member (the
 * server keeps an active admin while anyone else remains, and only admins and managers may read the roster), so
 * others get false without a call. Null when it can't be checked (offline, server trouble).
 */
export async function isOnlyMember(ctx: Pick<SessionCtx, 'role' | 'companyId' | 'authUserId'>): Promise<boolean | null> {
  if (ctx.role !== 'admin') return false;
  try {
    const roster = await callRpc<Roster>('team_roster', { p_company: ctx.companyId });
    const members = Array.isArray(roster?.members) ? roster.members : [];
    return members.every((m) => typeof m.user_id === 'string' && m.user_id.toLowerCase() === ctx.authUserId.toLowerCase());
  } catch {
    return null;
  }
}

/**
 * Leaves the company on the server (`leave_company`). Pending edits are pushed first (5 s at most) so the team
 * keeps them. `last_admin` when other members remain; for the last member the server deletes the company and all
 * its items. The caller then refreshes memberships (the account lands in No company and local data is wiped).
 */
export async function leaveCompany(ctx: Pick<SessionCtx, 'companyId'>): Promise<void> {
  await flushPending(5000);
  await callRpc<unknown>('leave_company', { p_company: ctx.companyId });
}

export type DeleteAccountOutcome = { ok: true };

/**
 * POST /functions/v1/delete-account with body {} (it runs `prepare_account_deletion`, then GoTrue's admin delete).
 * `{ok: true}` → deleted (the caller signs out). 409 `{ok: false, reason: 'last_admin', company}` → a ServerError
 * with code `last_admin` whose `company` names the company. A request that never got an answer throws NetworkError:
 * a dropped connection, a platform error without CORS headers, or (until the functions are deployed with CORS,
 * Appendix A item 1) every call from the live site.
 */
export async function deleteAccount(): Promise<DeleteAccountOutcome> {
  const answer = await invokeFunction('delete-account', {});
  const { status, body } = answer;
  const reason = typeof body?.reason === 'string' ? body.reason : null;
  const refused = status >= 200 && status < 300 ? body?.ok === false : status === 409;
  if (refused) {
    if (reason === 'last_admin') {
      const company = typeof body?.company === 'string' && body.company.trim() !== '' ? body.company.trim() : null;
      throw new LastAdminError(company);
    }
    throw new ServerError(409, reason, reason ?? "Your account couldn't be deleted.");
  }
  if (status < 200 || status >= 300) throw functionError(answer);
  return { ok: true };
}

export { LastAdminError };
